import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseLockIcon } from '@ubeya/shared-web/assets/lock.svg';
import { ReactComponent as BaseMultiDayIcon } from '@ubeya/shared-web/assets/multi-day-icon.svg';
import {
  NameRenderer as BaseNameRenderer,
  CellWithArrow as BaseCellWithArrow,
  smallBodySemiBold,
  TextWithTooltip,
  SmallBodySemiBold,
  FlexMiddle,
  Tooltip,
  MultipleOutsourceBadges
} from '@ubeya/shared-web/components';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { TIMESHEET_GROUP_BY_OPTIONS_ENUM } from '@ubeya/shared/constants';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { DAILY_EMPLOYEES_SLUG } from 'timeSheet/constants';

const CellWithArrow = styled(BaseCellWithArrow).attrs(() => ({ className: 'cell-with-arrow' }))`
  ${smallBodySemiBold};
  width: 100%;
`;

const ProjectNameWrapper = styled(FlexMiddle)``;

const DateText = styled.span`
  ${({ theme, isToday }) => isToday && `color: ${theme.colors.primary}`}
`;

const LockIcon = styled(BaseLockIcon)`
  min-width: 20px;
  margin-right: 3px;
`;

const MultiDayIcon = styled(BaseMultiDayIcon)`
  min-width: 16px;
  margin-right: 6px;

  path {
    fill: ${({ theme }) => theme.colors.disabled};
  }
`;

const ProjectName = styled(TextWithTooltip).attrs(() => ({ className: 'project-name-clickable' }))`
  &:hover {
    text-decoration: underline;
  }
`;

const OutsourceBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NameAggregateRenderer = (props) => {
  const { t, shortDateFormat } = useContext(ConfigContext);
  const { mappedPositions } = usePositions();
  const { accountId } = useAccount();
  const { mappedClients } = useCRM();
  const { mappedLocations } = useLocations();

  const {
    rowData: { summarized, totalRows, subRows, groupValue, isMultiDay },
    columnData: { groupBy, onClickEmployee },
    rowData,
    isExpended
  } = props;

  const isToday = useMemo(
    () => groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.DATE && moment(groupValue).isSame(moment(), 'day'),
    [groupBy, groupValue]
  );

  const formattedGroupByValue = useMemo(() => {
    switch (groupBy) {
      case TIMESHEET_GROUP_BY_OPTIONS_ENUM.DATE:
        return <DateText isToday={isToday}>{moment(groupValue).format(`${shortDateFormat}, ddd`)}</DateText>;

      case TIMESHEET_GROUP_BY_OPTIONS_ENUM.POSITION:
        return groupValue === 'undefined' || groupValue === 'null' ? t('other') : mappedPositions[groupValue]?.label;

      case TIMESHEET_GROUP_BY_OPTIONS_ENUM.PO_NUMBER:
      case TIMESHEET_GROUP_BY_OPTIONS_ENUM.COST_CENTER:
        return groupValue === 'undefined' || groupValue === 'null' ? '' : groupValue;

      default:
        return groupValue || '';
    }
  }, [groupBy, groupValue, isToday, mappedPositions, shortDateFormat, t]);

  if (summarized) {
    return totalRows ? <SmallBodySemiBold>{`${totalRows} ${t('total')}`}</SmallBodySemiBold> : <></>;
  }

  if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT) {
    const { projectId, branchId, projectIsInvoiceIssued, projectIsPayrollLock } = subRows[0];

    const handleProjectNameClick = (e) => {
      e.stopPropagation();

      if (!projectId || !branchId) {
        return;
      }

      window.open(`/${accountId}/scheduling/branch/${branchId}/project/${projectId}`, '_blank')?.focus();
    };

    const showLockIcon = projectIsInvoiceIssued || projectIsPayrollLock;
    const tooltipText = [];

    if (projectIsInvoiceIssued) {
      tooltipText.push(t('projectIsInvoiceIssued'));
    }

    if (projectIsPayrollLock) {
      tooltipText.push(t('projectIsPayrollLock'));
    }

    const formattedGroupValue = groupValue === DAILY_EMPLOYEES_SLUG ? t(groupValue) : groupValue;
    const trimmedProjectIdGroupValue = (formattedGroupValue ?? '').replace(`.${projectId}`, '');

    return (
      <CellWithArrow rowData={rowData} isExpended={isExpended}>
        <ProjectNameWrapper>
          {showLockIcon && (
            <Tooltip body={tooltipText.join(', ')}>
              <LockIcon />
            </Tooltip>
          )}

          {isMultiDay && (
            <Tooltip body={t('multiDayEvent')}>
              <MultiDayIcon />
            </Tooltip>
          )}

          <ProjectName onClick={handleProjectNameClick}>{trimmedProjectIdGroupValue}</ProjectName>
        </ProjectNameWrapper>
      </CellWithArrow>
    );
  }

  if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.MAIN_EVENT) {
    const hasMainEvent = groupValue && groupValue !== 'null' && groupValue !== 'undefined';

    const handleMainEventClick = (e) => {
      e.stopPropagation();
      if (!hasMainEvent) return;
      window.open(`/${accountId}/scheduling?mainProjectId=${groupValue}`, '_blank')?.focus();
    };

    const displayName = hasMainEvent ? subRows[0]?.mainProjectName || groupValue : '';

    return (
      <CellWithArrow rowData={rowData} isExpended={isExpended}>
        {hasMainEvent ? (
          <ProjectName onClick={handleMainEventClick}>{displayName}</ProjectName>
        ) : (
          <TextWithTooltip>{displayName}</TextWithTooltip>
        )}
      </CellWithArrow>
    );
  }

  if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.CLIENT) {
    return (
      <CellWithArrow rowData={rowData} isExpended={isExpended}>
        {mappedClients?.[groupValue]?.name || t('noClient')}
      </CellWithArrow>
    );
  }

  if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.EMPLOYEE) {
    const { employee, isExternal, projectId, shiftId, supplierData, originSupplierData } = subRows?.[0] || {};

    const isExternalParams = {
      isExternal,
      clientProjectId: projectId,
      supplierShiftId: shiftId
    };

    return (
      <CellWithArrow rowData={rowData} isExpended={isExpended}>
        <BaseNameRenderer
          rowData={employee}
          renderNext={
            isExternal ? (
              <OutsourceBadgeWrapper>
                <MultipleOutsourceBadges
                  supplierData={supplierData}
                  originSupplierData={originSupplierData}
                  withName={false}
                />
              </OutsourceBadgeWrapper>
            ) : undefined
          }
          columnData={{
            withUnderline: true,
            onClick: (e) => {
              e.stopPropagation();
              onClickEmployee?.({
                ...employee,
                ...(isExternal && isExternalParams)
              });
            }
          }}
        />
      </CellWithArrow>
    );
  }

  if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.LOCATION) {
    return (
      <CellWithArrow rowData={rowData} isExpended={isExpended}>
        {mappedLocations?.[groupValue]?.name || t('noLocations')}
      </CellWithArrow>
    );
  }

  return (
    <CellWithArrow rowData={rowData} isExpended={isExpended}>
      <TextWithTooltip>{formattedGroupByValue}</TextWithTooltip>
    </CellWithArrow>
  );
};

export default NameAggregateRenderer;
